import React, { useState, useEffect } from 'react';
import { createStorefrontApiClient } from '@shopify/storefront-api-client';
import { Context } from './context';
import { LocalStorage, LocalStorageKeys } from './utils';

const CART_CREATE_MUTATION = `
  mutation cartCreate($input: CartInput) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        totalQuantity
        lines(first: 100) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  product {
                    id
                    title
                    handle
                  }
                  image {
                    url
                    altText
                  }
                  price {
                    amount
                    currencyCode
                  }
                }
              }
            }
          }
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export function ContextProvider({ shopName, accessToken, children }) {
  if (shopName == null || accessToken == null) {
    throw new Error(
      'Unable to build shopify-buy client object. Please make sure that your access token and domain are correct.',
    );
  }

  const initialCart = LocalStorage.getInitialCart();
  const [cart, setCart] = useState(initialCart);
  const [client, setClient] = useState(null);
  const [isInitializing, setIsInitializing] = useState(false);

  const isCustomDomain = shopName.includes('.');
  const storeDomain = isCustomDomain ? shopName : `${shopName}.myshopify.com`;

  useEffect(() => {
    const shopifyClient = createStorefrontApiClient({
      storeDomain,
      publicAccessToken: accessToken,
      apiVersion: '2025-01',
    });
    setClient(shopifyClient);
  }, [storeDomain, accessToken]);

  useEffect(() => {
    async function createNewCart() {
      if (!client || isInitializing) return;

      setIsInitializing(true);
      try {
        const response = await client.request(CART_CREATE_MUTATION, {
          variables: { input: {} },
        });

        if (response?.data?.cartCreate?.cart) {
          const newCart = {
            id: response.data.cartCreate.cart.id,
            checkoutUrl: response.data.cartCreate.cart.checkoutUrl,
            totalQuantity: response.data.cartCreate.cart.totalQuantity || 0,
            lines:
              response.data.cartCreate.cart.lines?.edges?.map(
                (edge) => edge.node,
              ) || [],
            cost: response.data.cartCreate.cart.cost,
          };
          setCart(newCart);
        }
      } catch (error) {
        console.error('Error creating cart:', error);
      } finally {
        setIsInitializing(false);
      }
    }

    if (cart == null && client && !isInitializing) {
      console.log('Creating new cart');
      createNewCart();
    }
  }, [cart, client, isInitializing]);

  useEffect(() => {
    if (cart) {
      LocalStorage.set(LocalStorageKeys.CART, JSON.stringify(cart));
    }
  }, [cart]);

  return (
    <Context.Provider
      value={{
        client,
        cart,
        setCart,
        isInitializing,
      }}
    >
      {children}
    </Context.Provider>
  );
}
